import React, { useCallback, useState } from 'react';

import { BdsInput, BdsModal } from 'blip-ds/dist/blip-ds-react';
import { ModalContent, UrlInputContainer } from './styles';
import { KnowledgeBaseApi } from '../../../../api/KnowledgeBaseApi';
import { showToast } from '../../../../api/CommonApi';
import { useContentLocalizer } from '../../../../hooks/useContentLocalizer';
import { useModalManagement } from '../../../../hooks/useModalManagement';
import { useKnowledgeBaseTracking } from '../../../../hooks/useKnowledgeBaseTracking';
import { ModalHeader } from '../../../../components/ModalHeader';
import { ModalFooter } from '../../../../components/ModalFooter';
import { localization } from './localization';
import { useFileErrorHandler } from 'hooks/useFileErrorHandler';
import { KNOWLEDGE_CONTENT_SAVE, KNOWLEDGE_CONTENT_CANCEL } from '../../../../shared/SegmentEvents';

interface AddUrlModalProps {
  isOpen: boolean;
  tenantId: string;
  catalogId: string;
  onClose: (wasCancelled: boolean) => void;
}

const AddUrlModal: React.FC<AddUrlModalProps> = ({ isOpen, tenantId, catalogId, onClose }) => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { handleFileError } = useFileErrorHandler();
  const translate = useContentLocalizer(localization);
  const { trackKnowledgeBaseEvent } = useKnowledgeBaseTracking();

  const resetForm = useCallback(() => {
    setUrl('');
    setIsLoading(false);
  }, []);

  const handleTrackingCancel = useCallback(() => {
    if (isLoading) return;
    trackKnowledgeBaseEvent(KNOWLEDGE_CONTENT_CANCEL);
  }, [isLoading, trackKnowledgeBaseEvent]);

  const { modalRef, handleCancel } = useModalManagement({
    isOpen,
    onClose,
    resetForm,
    onCancel: handleTrackingCancel,
    shouldAllowCancel: !isLoading,
  });

  const handleUrlChange = useCallback((event: CustomEvent) => {
    setUrl(event.detail.value);
  }, []);

  const handleAddUrl = useCallback(async () => {
    if (!isFormValid || isLoading || !url) {
      return;
    }

    try {
      setIsLoading(true);

      // First try addUrlDocumentAsync
      try {
        await KnowledgeBaseApi.addUrlDocumentAsync(tenantId, catalogId, url);
      } catch (firstError) {
        // If first method fails, try addUrlDocumentCrawlAsync as fallback
        // eslint-disable-next-line no-console
        console.warn('addUrlDocumentAsync failed, trying addUrlDocumentCrawlAsync:', firstError);
        await KnowledgeBaseApi.addUrlDocumentCrawlAsync(tenantId, catalogId, url);
      }

      trackKnowledgeBaseEvent(KNOWLEDGE_CONTENT_SAVE, {
        knowcontName: new URL(url).hostname,
        knowcontType: 'url',
      });

      showToast({
        title: translate.successToast.title,
        message: translate.successToast.message,
        type: 'success',
      });

      resetForm();
      onClose(false);
    } catch (error) {
      const consoleError = 'Error adding URL (both methods failed):';
      handleFileError(consoleError, translate.errorToast, error as string);
    } finally {
      setIsLoading(false);
    }
  }, [url, tenantId, catalogId, onClose, resetForm, isLoading, translate, trackKnowledgeBaseEvent, handleFileError]);

  const isFormValid = url.trim() !== '';

  return (
    <BdsModal
      ref={modalRef}
      open={isOpen}
      size="dynamic"
      closeButton={!isLoading}
      outzoneClose={!isLoading}
      enterClose={false}
    >
      <ModalContent direction="column" gap="3">
        <ModalHeader title={translate.title} subtitle={translate.subtitle} />

        <UrlInputContainer>
          <BdsInput
            type="text"
            placeholder={translate.input.placeholder}
            value={url}
            onBdsChange={handleUrlChange}
            required={true}
            requiredErrorMessage={translate.input.requiredErrorMessage}
            disabled={isLoading}
          />
        </UrlInputContainer>

        <ModalFooter
          cancelText={translate.cancelButton}
          saveText={translate.saveButton}
          isLoading={isLoading}
          isFormValid={isFormValid}
          onCancel={handleCancel}
          onSave={handleAddUrl}
        />
      </ModalContent>
    </BdsModal>
  );
};

export default AddUrlModal;
